import { getPlansList } from '../services/plans/listPlans';
import {
  CreateGridRequest,
  Insurance,
  InsurancePlan,
  Plan,
} from '../types/plans/types';
import { parseBackendAgeString } from './ageRanges';

/**
 * Fetches insurance plans for a source provider-practice combination
 */
export const fetchSourceInsurancePlans = async (
  providerId: string,
  practiceId: string
): Promise<InsurancePlan[]> => {
  try {
    const result = await getPlansList(null, {
      providerIds: [providerId],
      practiceIds: [practiceId],
    });

    if (!result.data?.insurancePlans) {
      return [];
    }

    return result.data.insurancePlans;
  } catch (error) {
    console.error('Failed to fetch source insurance plans:', error);
    throw new Error('Failed to fetch source insurance plans');
  }
};

/**
 * Extracts age range from a plan, handling various formats
 */
const extractAgeRange = (plan: Plan): {
  minimumAge: number | null;
  maximumAge: number | null;
} => {
  // Prefer explicit numeric fields if available
  if (
    plan.minimumAge !== undefined ||
    plan.maximumAge !== undefined
  ) {
    return {
      minimumAge: plan.minimumAge ?? null,
      maximumAge: plan.maximumAge ?? null,
    };
  }

  // Fall back to parsing age string
  if (plan.age) {
    const ageRange = parseBackendAgeString(plan.age);
    return {
      minimumAge: ageRange.min ?? null,
      maximumAge: ageRange.max ?? null,
    };
  }

  // Try to extract from metadata if available
  if (plan.metadata && typeof plan.metadata === 'object') {
    const metadata = plan.metadata as {
      minimumAge?: number | null;
      maximumAge?: number | null;
    };
    if (
      metadata.minimumAge !== undefined ||
      metadata.maximumAge !== undefined
    ) {
      return {
        minimumAge: metadata.minimumAge ?? null,
        maximumAge: metadata.maximumAge ?? null,
      };
    }
  }

  // Default to null/null (all ages)
  return {
    minimumAge: null,
    maximumAge: null,
  };
};

/**
 * Transforms source insurance plans into CreateGridRequest[] format
 */
export const prepareGridEntries = (
  sourcePlans: InsurancePlan[],
  targetProviderId: string,
  targetPracticeId: string,
  selectedPlanIds?: Set<string>
): CreateGridRequest[] => {
  const gridEntries: CreateGridRequest[] = [];

  for (const insurancePlan of sourcePlans) {
    for (const plan of insurancePlan.plans) {
      // If plan selection is provided, only include selected plans
      if (selectedPlanIds && !selectedPlanIds.has(plan.id)) {
        continue;
      }

      const ageRange = extractAgeRange(plan);

      gridEntries.push({
        practiceId: targetPracticeId,
        providerId: targetProviderId,
        plan_id: plan.insurance.id,
        credentialStatus: plan.credentialstatus || 'Pending',
        value: plan.value || 'No Status',
        minimumAge: ageRange.minimumAge,
        maximumAge: ageRange.maximumAge,
      });
    }
  }

  return gridEntries;
};

/**
 * Validates that a copy operation is valid
 */
export const validateCopyOperation = (
  sourceProviderId: string,
  sourcePracticeId: string,
  targetProviderId: string,
  targetPracticeId: string
): { valid: boolean; error?: string } => {
  if (!sourceProviderId || !sourcePracticeId) {
    return {
      valid: false,
      error: 'Source provider and practice are required',
    };
  }

  if (!targetProviderId || !targetPracticeId) {
    return {
      valid: false,
      error: 'Target provider and practice are required',
    };
  }

  // Prevent copying to the same provider-practice combination
  if (
    sourceProviderId === targetProviderId &&
    sourcePracticeId === targetPracticeId
  ) {
    return {
      valid: false,
      error: 'Cannot copy insurance to the same provider-practice combination',
    };
  }

  return { valid: true };
};

/**
 * Checks if insurance plan already exists for target provider-practice
 */
export const checkExistingPlans = async (
  targetProviderId: string,
  targetPracticeId: string,
  planIds: string[]
): Promise<Set<string>> => {
  try {
    const result = await getPlansList(null, {
      providerIds: [targetProviderId],
      practiceIds: [targetPracticeId],
    });

    if (!result.data?.insurancePlans) {
      return new Set();
    }

    const existingPlanIds = new Set<string>();
    for (const insurancePlan of result.data.insurancePlans) {
      for (const plan of insurancePlan.plans) {
        if (planIds.includes(plan.insurance.id)) {
          existingPlanIds.add(plan.insurance.id);
        }
      }
    }

    return existingPlanIds;
  } catch (error) {
    console.error('Failed to check existing plans:', error);
    // Return empty set on error to allow operation to proceed
    return new Set();
  }
};

/**
 * Fetches all available insurance plans for a given state
 * Returns unique Insurance objects (by insurance.id)
 */
export const fetchAvailableInsurancePlans = async (
  state: string
): Promise<Insurance[]> => {
  try {
    const result = await getPlansList(null, {
      state: [state],
    }, 1000); // Fetch up to 1000 records

    if (!result.data?.insurancePlans) {
      return [];
    }

    // Extract unique insurance plans by insurance.id
    const insuranceMap = new Map<string, Insurance>();
    
    for (const insurancePlan of result.data.insurancePlans) {
      for (const plan of insurancePlan.plans) {
        const insuranceId = plan.insurance.id;
        if (!insuranceMap.has(insuranceId)) {
          insuranceMap.set(insuranceId, plan.insurance);
        }
      }
    }

    return Array.from(insuranceMap.values());
  } catch (error) {
    console.error('Failed to fetch available insurance plans:', error);
    throw new Error('Failed to fetch available insurance plans');
  }
};
